import { useMemo } from "react";

import type { ClaimRegisterResponse } from "@tm/insurance/api";

import FormattedText, {
  getPlainText,
  type FormattedTextProps,
  type TextNodeType,
} from "../../../../components/FormattedText/FormattedText";
import type {
  ClaimResultClaimCard,
  ClaimResultVariant,
  ClaimSubmitResultData,
  TextNode,
} from "../../types";
import { CLAIM_ACTION, CLAIM_RESULT_COMPONENT_ID } from "../../types";
import {
  Animation,
  ClaimCardBox,
  ClaimCardMeta,
  ClaimCardTitle,
  FullWidthPrimaryButton,
  ReferenceValue,
  ResultContent,
  ResultCopy,
  ResultPage,
  SecondaryLink,
  Title,
} from "./ClaimSubmitResult.style";

export interface ClaimSubmitResultProps {
  response: ClaimRegisterResponse;
  onDone: () => void;
  onRetry: () => void;
}

type TextValue = FormattedTextProps["value"];

function asTextValue(value?: string | TextNode): TextValue {
  if (value == null || value === "") return undefined;
  return value as TextValue;
}

function plain(value?: string | TextNode): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  return getPlainText(value as TextNodeType);
}

function buildClaimCardMeta(claimCard: ClaimResultClaimCard): string {
  const parts: string[] = [];
  const typeValue = plain(claimCard.claimType?.value).trim();
  if (typeValue) {
    const label = plain(claimCard.claimType?.label).trim();
    parts.push(label ? `${label} ${typeValue}` : typeValue);
  }
  const pax = plain(claimCard.paxInfo).trim();
  if (pax) parts.push(pax);
  return parts.join(" • ");
}

export function ClaimSubmitResult({
  response,
  onDone,
  onRetry,
}: ClaimSubmitResultProps) {
  const isSuccess = response.registrationStatus === "SUCCESS";

  const variant = useMemo<ClaimResultVariant | undefined>(() => {
    const data = response.components?.find(
      (c) => c.id === CLAIM_RESULT_COMPONENT_ID
    )?.data as ClaimSubmitResultData | undefined;
    return isSuccess ? data?.success : data?.failure;
  }, [response.components, isSuccess]);

  const runAction = (actionId?: string) => {
    if (actionId === CLAIM_ACTION.TRY_AGAIN) return onRetry();
    return onDone();
  };

  const claimCard = variant?.claimCard;
  const claimCardMeta = claimCard ? buildClaimCardMeta(claimCard) : "";
  const claimCardTitle = asTextValue(claimCard?.title);
  const showClaimCard = Boolean(
    claimCard && (claimCardTitle || claimCardMeta || claimCard.icon)
  );
  const secondaryCta = variant?.secondaryCta;
  const primaryCta = variant?.primaryCta;
  const refLabel = plain(variant?.referenceNumber?.label).trim();
  const refValue = asTextValue(variant?.referenceNumber?.value);
  const title = asTextValue(variant?.title);
  const subtitle = asTextValue(variant?.subtitle);
  const refundMessage = asTextValue(variant?.refundMessage);
  const primaryCtaText = asTextValue(primaryCta?.text);
  const secondaryCtaText = asTextValue(secondaryCta?.text);

  return (
    <ResultPage>
      <ResultContent>
        {variant?.animationUrl ? (
          <Animation src={variant.animationUrl} alt="" />
        ) : null}

        {title ? (
          <Title>
            <FormattedText value={title} />
          </Title>
        ) : null}
        {subtitle ? (
          <ResultCopy>
            <FormattedText value={subtitle} />
          </ResultCopy>
        ) : null}

        {refValue ? (
          <ResultCopy $mb="28px">
            {refLabel ? (
              <>
                <FormattedText
                  value={asTextValue(variant?.referenceNumber?.label)}
                />
                {!refLabel.endsWith(":") ? ": " : " "}
              </>
            ) : null}
            <ReferenceValue>
              <FormattedText value={refValue} />
            </ReferenceValue>
          </ResultCopy>
        ) : null}

        {showClaimCard ? (
          <ClaimCardBox>
            <div>
              {claimCardTitle ? (
                <ClaimCardTitle>
                  <FormattedText value={claimCardTitle} />
                </ClaimCardTitle>
              ) : null}
              {claimCardMeta ? (
                <ClaimCardMeta>{claimCardMeta}</ClaimCardMeta>
              ) : null}
            </div>
            {claimCard?.icon ? <img src={claimCard.icon} alt="" /> : null}
          </ClaimCardBox>
        ) : null}

        {refundMessage ? (
          <ResultCopy $mb="24px" $maxWidth="420px" $size="13px" $lh="1.5">
            <FormattedText value={refundMessage} />
          </ResultCopy>
        ) : null}

        {primaryCtaText ? (
          <FullWidthPrimaryButton
            type="button"
            onClick={() => runAction(primaryCta?.actionId)}
          >
            <FormattedText value={primaryCtaText} />
          </FullWidthPrimaryButton>
        ) : null}

        {secondaryCtaText ? (
          <SecondaryLink
            type="button"
            onClick={() => runAction(secondaryCta?.actionId)}
          >
            <FormattedText value={secondaryCtaText} />
          </SecondaryLink>
        ) : null}
      </ResultContent>
    </ResultPage>
  );
}
